import React from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Modal from './ui/Modal';

const ServiceSelectionModal = ({ 
  isOpen, 
  onClose, 
  services, 
  onSelectService, 
  title = "Choisir un service",
  message = "Sélectionnez le pack ou abonnement à utiliser pour cette réservation :"
}) => {
  const getServiceTypeLabel = (type) => {
    return type === 'pack' ? 'Pack' : 'Abonnement';
  };

  const getServiceStatus = (service) => {
    const now = new Date();
    const endDate = new Date(service.end_date);
    
    if (service.status !== 'active') {
      return { label: 'Inactif', color: 'text-red-600 bg-red-50' };
    }
    
    if (endDate < now) {
      return { label: 'Expiré', color: 'text-red-600 bg-red-50' };
    }
    
    if (service.remaining_sessions <= 0) {
      return { label: 'Épuisé', color: 'text-orange-600 bg-orange-50' };
    }
    
    return { label: 'Actif', color: 'text-green-600 bg-green-50' };
  };

  const isServiceUsable = (service) => {
    const now = new Date();
    const endDate = new Date(service.end_date);
    
    return service.status === 'active' && 
           endDate >= now && 
           service.remaining_sessions > 0;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        <p className="text-gray-600 text-sm">
          {message}
        </p>

        {services.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">
              <CheckCircleIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun service actif
            </h3>
            <p className="text-gray-600">
              Vous devez avoir un service actif pour effectuer une réservation.
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {services.map((service) => {
              const status = getServiceStatus(service);
              const isUsable = isServiceUsable(service);
              
              return (
                <div
                  key={service.id}
                  className={`border rounded-lg p-4 transition-all ${
                    isUsable 
                      ? 'border-gray-200 hover:border-primary-300 hover:shadow-md cursor-pointer' 
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                  }`}
                  onClick={() => isUsable && onSelectService(service)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {service.service_type.name}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-600">
                          {getServiceTypeLabel(service.service_type.type)}
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4" />
                          <span>
                            Sessions restantes: {service.remaining_sessions}
                            {service.service_type.type === 'pack' && 
                              ` / ${service.total_sessions}`
                            }
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <CalendarDaysIcon className="h-4 w-4" />
                          <span>
                            Valide jusqu'au {format(new Date(service.end_date), 'd MMMM yyyy', { locale: fr })}
                          </span>
                        </div>
                        
                        {service.service_type.type === 'abonnement' && (
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-4 w-4" />
                            <span>
                              Ce mois: {service.current_month_used || 0} / {service.current_month_sessions || service.service_type.session_count} sessions utilisées
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {isUsable && (
                      <div className="ml-4">
                        <CheckCircleIcon className="h-5 w-5 text-primary-600" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ServiceSelectionModal;
